import { assertUnreachable } from '@sgme/fp';
import {
  addDays,
  addMonths,
  addWeeks,
  endOfDay,
  endOfMonth,
  endOfWeek,
  formatISO,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from 'date-fns';

import type { ISODateTime } from '@/types/date.ts';

export const BLOTTER_DATERANGE_CUSTOM_PRESETS_IDS = [
  'yesterday',
  'thisWeek',
  'lastWeek',
  'thisMonth',
  'lastMonth',
] as const;

export type BlotterFilterPresetRange = (typeof BLOTTER_DATERANGE_CUSTOM_PRESETS_IDS)[number];

// some of these custom presets are already implemented in sgbootstrap daterangepicker (builtin)
// we could use them and implement others
// but the sgbootstrap daterangepicker will put the custom presets first in the list, and the builtin presets after
// breaking the natural presentaton logic of these presets
export function computeRangeForCustomPreset(presetId: BlotterFilterPresetRange): [EpochTimeStamp, EpochTimeStamp] {
  switch (presetId) {
    case 'yesterday': {
      const nowMinus24h = addDays(new Date(), -1);
      return [startOfDay(nowMinus24h).getTime(), endOfDay(nowMinus24h).getTime()];
    }
    case 'thisWeek':
      return [startOfWeek(new Date(), { weekStartsOn: 1 }).getTime(), endOfDay(new Date()).getTime()];
    case 'lastWeek': {
      const nowMinus7days = addWeeks(new Date(), -1);
      return [
        startOfWeek(nowMinus7days, { weekStartsOn: 1 }).getTime(),
        endOfWeek(nowMinus7days, { weekStartsOn: 1 }).getTime(),
      ];
    }
    case 'thisMonth':
      return [startOfMonth(new Date()).getTime(), endOfDay(new Date()).getTime()];
    case 'lastMonth': {
      const nowMinus1month = addMonths(new Date(), -1);
      return [startOfMonth(nowMinus1month).getTime(), endOfMonth(nowMinus1month).getTime()];
    }
    default:
      return assertUnreachable(presetId, `unknown value ${presetId}`);
  }
}

export function computeRangeForCustomPresetISO(presetId: BlotterFilterPresetRange): [ISODateTime, ISODateTime] {
  const [fromTS, toTS] = computeRangeForCustomPreset(presetId);
  return [formatISO(fromTS), formatISO(toTS)];
}
